import * as React from 'react'

import findIndex from 'lodash/findIndex'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

import type { MunchedData } from 'modules/calendar/lib/munchers'
import type { CalendarViewEvent } from 'modules/calendar/lib/munchers/types'

import DateCellWrapper from './components/DateCellWrapper'
import MonthDate from './components/MonthDate'
import MonthEvent from './components/MonthEvent'
import LeaveStore from './components/MonthEvent/LeaveStore'

interface EventProps {
  event: CalendarViewEvent
}
interface CellWrapperProps {
  value: Date
  range: Date[]
}
interface DateHeaderProps {
  date: moment.Moment
  isOffRange: boolean
}
interface ReturnType {
  event: (eventProps: EventProps) => React.ReactNode
  dateCellWrapper: (cellWrapperProps: React.PropsWithChildren<CellWrapperProps>) => React.ReactNode
  month: {
    dateHeader: (dateHeaderProps: DateHeaderProps) => React.ReactNode
  }
}
interface Props {
  munchedData: MunchedData
  canEditBankHolidays: boolean
}

const useCalendarComponents = ({ munchedData, canEditBankHolidays }: Props): ReturnType => {
  const leaveStore = React.useMemo(() => new LeaveStore(), [])
  const { leaves: groupedLeaves, bubbles, companyHolidays } = munchedData

  return {
    event: function renderMonthEvent({ event }: EventProps) {
      return <MonthEvent event={event} leaveStore={leaveStore} />
    },
    dateCellWrapper: function renderCellWrapper({
      children,
      value,
      range,
    }: React.PropsWithChildren<CellWrapperProps>) {
      const index = findIndex(range, (d) => value === d)
      const isFirst = index === 0
      const isLast = index === range.length - 1
      const day = moment(value)
      const events = bubbles[dateToString(day, 'backend')] || []
      const holidays = companyHolidays[dateToString(day, 'backend')] || []
      const leavesForDate = groupedLeaves[dateToString(day, 'backend')] || []

      return (
        <DateCellWrapper
          day={day}
          holidays={holidays}
          events={events}
          leaves={leavesForDate}
          isFirst={isFirst}
          isLast={isLast}
          canEditBankHolidays={canEditBankHolidays}
        >
          {children}
        </DateCellWrapper>
      )
    },
    month: {
      dateHeader: function renderDateHeader({ date, isOffRange }: DateHeaderProps) {
        const day = moment(date)
        const events = bubbles[dateToString(day, 'backend')] || []
        const holidays = companyHolidays[dateToString(day, 'backend')] || []

        return (
          <MonthDate day={day} events={events} holidays={holidays} isAnotherMonth={isOffRange} />
        )
      },
    },
  }
}

export default useCalendarComponents
